import { BaseCard, CardSection, ErrorCard } from "@fero/components";
import {
  useGetNominatedPurchasersForOrgQuery,
  useGetPurchaserSavedProductPricesQuery,
  useSaveNominatedPurchaserDiscountMutation,
} from "@fero/hooks";
import { usePurchaserSalePriceStore } from "@fero/stores";
import EditIcon from "@mui/icons-material/EditOutlined";
import { IconButton, Skeleton, Stack, Typography } from "@mui/material";
import type React from "react";
import { useState } from "react";
import { usePurchaserSalePriceHelpers } from "../hooks";
import { PurchaserDiscountsModal } from "./purchaser-discounts-modal";

interface PurchaserDiscountsCardProps {
  nominatedPurchaserId: string;
}

interface CustomPriceListProps {
  organisationId: string;
  purchaserId: string;
}

const CustomPriceList: React.FC<CustomPriceListProps> = ({
  organisationId,
  purchaserId,
}) => {
  const {
    data: allProductSalePrices,
    isLoading: isLoadingProductSalePrices,
    isError: isErrorGetSalePrice,
  } = useGetPurchaserSavedProductPricesQuery({ organisationId, purchaserId });

  if (isLoadingProductSalePrices) {
    return (
      <Skeleton variant="rounded" height={30} width={200} animation="wave" />
    );
  }

  if (isErrorGetSalePrice) {
    return <Typography>Unable to load custom price list...</Typography>;
  }

  if (allProductSalePrices.body.length > 0) {
    return <Typography>Custom Price List Applied</Typography>;
  }

  return <Typography>No custom prices for this purchaser</Typography>;
};

export const PurchaserDiscountsCard: React.FC<PurchaserDiscountsCardProps> = (
  props,
) => {
  const { nominatedPurchaserId } = props;
  const [editDiscountsOpen, setEditDiscountsOpen] = useState(false);
  const { saveProductSalePrices } = usePurchaserSalePriceHelpers();
  const saveNominatedPurchaserDiscountMutation =
    useSaveNominatedPurchaserDiscountMutation();
  const { purchaserDiscount } = usePurchaserSalePriceStore();
  const {
    data: nominatedPurchasers = [],
    isLoading,
    isError,
  } = useGetNominatedPurchasersForOrgQuery();
  const nominatedPurchaser = nominatedPurchasers.find(
    (np) => np.nominatedPurchaserId === nominatedPurchaserId,
  );

  if (isLoading) {
    return <Skeleton variant="rounded" height={130} animation="wave" />;
  }

  if (isError) {
    return <ErrorCard message="Error loading discounts info" />;
  }

  if (!nominatedPurchaser) {
    return (
      <BaseCard spacing={3}>
        <Typography variant="h6" fontWeight={500} fontSize={20}>
          Discounts
        </Typography>
        <Typography>No discount details found for purchaser</Typography>
      </BaseCard>
    );
  }

  const onSaveProductSalePrices = async () => {
    await saveProductSalePrices();
    await saveNominatedPurchaserDiscountMutation.mutateAsync({
      body: {
        purchaserDiscount: purchaserDiscount,
        nominatedPurchaserId: nominatedPurchaserId,
      },
    });
    setEditDiscountsOpen(false);
  };

  return (
    <>
      <PurchaserDiscountsModal
        isOpen={editDiscountsOpen}
        onSubmit={onSaveProductSalePrices}
        onClose={() => setEditDiscountsOpen(false)}
      />
      <BaseCard spacing={3}>
        <Stack
          direction="row"
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant="h6" fontWeight={500} fontSize={20}>
            Discounts
          </Typography>
          <IconButton onClick={() => setEditDiscountsOpen(true)}>
            <EditIcon />
          </IconButton>
        </Stack>

        <CardSection sectionTitle="Default Discount">
          <Typography>
            {nominatedPurchaser.savedDiscountPct
              ? `${nominatedPurchaser.savedDiscountPct}% - Applied Per Item Purchased`
              : "No discount applied to this purchaser"}
          </Typography>
        </CardSection>
        <CustomPriceList
          organisationId={nominatedPurchaser.organisationId}
          purchaserId={nominatedPurchaser.purchaserId}
        />
      </BaseCard>
    </>
  );
};
